﻿const defaultResolveTimeout = 1500;
const defaultPurchaseAffiliation = 'LF - tecknade försäkringar';
const defaultPurchaseFailedAffiliation = 'LF - misslyckade tecknanden';

let instance = null;

export default class AnalyticsService {
    constructor() {
        if (instance) {
            return instance; 
        }

        this.ready = typeof window.dataLayer !== 'undefined' && typeof window.dataLayer.push === 'function';

        instance = {
            trackInsurancePurchase: this.trackInsurancePurchase,
            trackVirtualPageView: this.trackVirtualPageView
        };

        return instance; 
    }

    // public 

    trackInsurancePurchase = (orders, memberInfo) => {
        const completedOrders = [...orders.completed, ...orders.pending];
        const failedOrders = orders.failed;

        // split completed and failed orders into new and upgrade categories

        const completedNewOrders = this.filterOrders(completedOrders, order => 
            typeof order.isUpgrade === 'undefined' || !order.isUpgrade
        );

        const completedUpgradeOrders = this.filterOrders(completedOrders, order => 
            typeof order.isUpgrade !== 'undefined' && order.isUpgrade
        );

        const failedNewOrders = this.filterOrders(failedOrders, order => 
            typeof order.isUpgrade === 'undefined' || !order.isUpgrade
        );

        const failedUpgradeOrders = this.filterOrders(failedOrders, order => 
            typeof order.isUpgrade !== 'undefined' && order.isUpgrade
        );

        const orderId = `${Date.now()}-success`;
        const upgradeOrderId = `${Date.now()}-upgrade-success`;
        const failedOrderId = `${Date.now()}-fail`;
        const upgradeFailedOrderId = `${Date.now()}-upgrade-fail`;

        return Promise.all([
            this.trackInsurancePurchaseCategory(orderId, defaultPurchaseAffiliation, completedNewOrders),
            this.trackInsurancePurchaseCategory(upgradeOrderId, defaultPurchaseAffiliation, completedUpgradeOrders),
            this.trackInsurancePurchaseCategory(failedOrderId, defaultPurchaseFailedAffiliation, failedNewOrders),
            this.trackInsurancePurchaseCategory(upgradeFailedOrderId, defaultPurchaseFailedAffiliation, failedUpgradeOrders)
        ]);
    }

    // NOTE: the "virtualPageView" gtm/mtm event has to be a trigger for a ga page view, also,
    // virtualPageTitle must map to the field "title" and virtualPagePath to the "page" field.

    trackVirtualPageView = this.promisifyWithTimeout((resolve, pageTitle, pagePath) => {
        if (!this.ready) {
            resolve();
            return; 
        }

        dataLayer.push({
            event: 'virtualPageView',
            virtualPageTitle: pageTitle,
            virtualPagePath: pagePath,
            eventCallback: () => {
                resolve();
            }
        });
    }); 

    // private 

    filterOrders(orders, filterFn) {
        return orders.map(groupOrder =>
            (Array.isArray(groupOrder.insurances)
                ? groupOrder.insurances
                : [groupOrder.insurances]
            )
            .filter(order => filterFn(order))
        ).reduce((acc, val) => val ? acc.concat(val) : acc, []); // flatten final array
    }

    trackInsurancePurchaseCategory = this.promisifyWithTimeout((resolve, orderId, category, orders) => {
        if (!this.ready || orders.length === 0) {
            resolve();
            return; 
        }

        const products = orders.map(order => ({
            name: order.displayName,
            id: orderId,
            sku: order.insuranceName,
            category: order.groupName,
            quantity: 1,
            price: order.premiumPeriod === 'monthly'
                ? order.quote
                : 0
        }));

        const totalRevenue = products
            .reduce((sum, prod) => sum + parseFloat(prod.price), 0); 

        // push to ga via gtm/mtm
        // NOTE: the "insurancePurchase" event has to be a trigger for a transaction type universal ga tag in gtm/mtm admin!

        dataLayer.push({
            event: 'insurancePurchase',
            transactionId: orderId,
            transactionAffiliation: category,
            transactionTotal: totalRevenue,
            transactionTax: 0,
            transactionShipping: 0,
            transactionProducts: products,
            eventCallback: () => {
                resolve();
            }
        });
    });

    // promisify a function with an automatic resolve timeout
    // NOTE: func must provide arg "resolve" as first param (the resolve function) when declared
    // the returned final function accepts all original params except "resolve"!

    promisifyWithTimeout(func) {
        return (...args) => new Promise((resolve) => {
                let gaTimeout = setTimeout(() => {
                    console.warn('[LF] Google analytics transaction timed out or was cancelled');
                    resolve();
                }, defaultResolveTimeout);

                func(() => {
                    clearTimeout(gaTimeout);
                    resolve();
                }, ...args);
            })
            .catch(e => {
                console.warn('[LF] Error sending Google analytics transaction!');
                console.dir(e);
            });
    }
}